import type { AccessTier } from './AccessTier'
import { AccessDeniedError } from './AccessTier'
import type { PromptLoginResult } from './AccessGateApi'

export interface AccessGateConfig {
  isAnonymous: () => boolean
  promptLogin: () => Promise<PromptLoginResult>
  isAutoPromptEnabled: () => boolean
}

export function createAccessGatedApi<T extends object>(
  api: T,
  gatedMethods: (keyof T)[],
  requiredTier: AccessTier,
  config: AccessGateConfig,
): T {
  const gatedSet = new Set<string | symbol>(gatedMethods as (string | symbol)[])

  return new Proxy(api, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver)

      if (!gatedSet.has(prop) || typeof value !== 'function') {
        return value
      }

      return function gatedCall(this: unknown, ...args: unknown[]) {
        // Authenticated users pass straight through
        if (!config.isAnonymous()) {
          return value.apply(target, args)
        }

        // All gated methods are async (sync methods like isEnabled must
        // not be in the gated list). Return rejected/resolved Promises so
        // callers can always use .catch() / await consistently.
        const action: 'prompt_login' | 'prompt_age_gate' = 'prompt_login'

        if (!config.isAutoPromptEnabled()) {
          return Promise.reject(new AccessDeniedError(requiredTier, action))
        }

        return (async () => {
          const loginResult = await config.promptLogin()

          if (!loginResult.success) {
            throw new AccessDeniedError(requiredTier, action)
          }

          return value.apply(target, args)
        })()
      }
    },
  })
}
